import withLoading from '@oncolens/oncolens-ui/lib/components/Common/withLoading';
import { SectionTitle } from '@/components/Layout/SectionTitle';
import { EventHistoryDto, useGetEventHistories } from '@/features/requests/api/getEventHistories';

interface EventHistoryProps {
	patientId: string | undefined;
}

interface EventHistoryBodyProps {
	data: EventHistoryDto[];
}

export const EventHistory = ({ patientId }: EventHistoryProps) => {
  const eventHistories = useGetEventHistories(patientId);

  return (
    <div className="print:hidden">
      <SectionTitle title="Event History" />
      <EventHistoryBodyWithLoading
        isLoading={eventHistories.isLoading}
        data={eventHistories.data ?? ([] as EventHistoryDto[])}
      />
    </div>
  );
};

const EventHistoryBody = ({ data }: EventHistoryBodyProps) => {
  return data.length > 0 ? (
    <table data-cy="event-history-table" className="w-full table-auto">
      <thead data-cy="event-history-headers">
        <tr>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Event
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
            Status
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Date
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
            Comments
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Tissue Availability
          </th>
          <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
						Collection Site
          </th>
        </tr>
      </thead>
      <tbody data-cy="event-history-values" className="text-xs">
        {data.map((eventHistory) => {
          return (
            <tr data-cy="event-history" key={eventHistory.id}>
              <td data-cy="event-history-type" className="p-2 w-1/8">{eventHistory.eventType}</td>
              <td data-cy="event-history-status" className="p-2 w-1/8">{eventHistory.status}</td>
              <td data-cy="event-history-date" className="p-2 w-1/8">{eventHistory.eventDate?.toLocaleDateString()}</td>
              <td data-cy="event-history-comments" className="p-2 w-3/8">{eventHistory.comments}</td>
              <td data-cy="event-history-tissue-availability" className="p-2 w-1/8">
                {eventHistory.biopsyIsTissueAvailable === null
                  ? '-'
                  : eventHistory.biopsyIsTissueAvailable
                    ? 'Yes'
                    : 'No'}
              </td>
              <td data-cy="event-history-collection-site" className="p-2 w-1/8">{eventHistory.biopsyCollectionSite || '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  ) : (
    <div className="w-full py-8 bg-gray-100 rounded-lg">
      <p className="w-full text-center">No event history has been provided yet.</p>
    </div>
  );
};

const EventHistorySkeleton = () => (
  <table className="w-full table-auto">
    <thead>
      <tr>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Event
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
          Status
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Date
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
          Comments
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Tissue Availability
        </th>
        <th className="px-2 pt-1 text-sm font-semibold text-left normal-case border-b border-b-gray-200">
					Collection Site
        </th>
      </tr>
    </thead>
    <tbody className="text-xs">
      <tr>
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
        <td className="h-5 bg-gray-300 rounded-lg animate-pulse" />
      </tr>
    </tbody>
  </table>
);

const EventHistoryBodyWithLoading = withLoading(EventHistoryBody, EventHistorySkeleton);
